import { Lock, Smartphone, Users } from "lucide-react"

import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

const billItems = [
  { key: "pastor", paid: false },
  { key: "beer", paid: true },
  { key: "aguachile", paid: false },
  { key: "jamaica", paid: false },
]

/**
 * The hero's illustration is a comanda — the paper ticket that crosses a
 * restaurant during service, which DESIGN.md names as the system's creative
 * north star. It is drawn from the same tokens as the product, so it needs no
 * photography: the assets the landing used to wait on were four dashed
 * placeholders carrying image-generation prompts.
 *
 * Every figure here is example content and lives in the locale file, the same
 * way the previous console's counters did.
 */
export function HeroBill() {
  return (
    <div className="relative mx-auto w-full max-w-[29rem] self-start lg:mx-0 lg:justify-self-end">
      <div className="overflow-hidden rounded-xl border bg-card shadow-lg">
        <div className="flex items-center justify-between gap-4 border-b px-5 py-4">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-medium text-muted-foreground">
              {t("ui.landing.hero_bill.status_label")}
            </span>
            <span className="text-base font-semibold tracking-tight">{t("ui.landing.hero_bill.table")}</span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium">
            <span className="size-2 rounded-full bg-status-available" aria-hidden />
            {t("ui.landing.hero_bill.live")}
          </span>
        </div>

        <div className="flex items-center gap-2 border-b bg-brand-subtle px-5 py-2.5">
          <Users className="size-3.5 shrink-0 text-brand" aria-hidden />
          <span className="text-xs font-medium text-foreground">{t("ui.landing.hero_bill.entry_note")}</span>
        </div>

        <ul>
          {billItems.map((item) => (
            <li
              key={item.key}
              className={cn(
                "grid grid-cols-[1.5rem_minmax(0,1fr)_auto] items-baseline gap-3 border-b px-5 py-3",
                item.paid && "bg-muted"
              )}
            >
              <span className="text-sm font-medium tabular-nums text-muted-foreground">
                {t(`ui.landing.hero_bill.items.${item.key}.quantity`)}
              </span>
              <span className="flex flex-col gap-0.5">
                <span
                  className={cn(
                    "flex items-center gap-2 text-sm font-medium",
                    item.paid && "text-muted-foreground"
                  )}
                >
                  {t(`ui.landing.hero_bill.items.${item.key}.name`)}
                  {item.paid ? <Lock className="size-3 text-success" aria-hidden /> : null}
                </span>
                <span className={cn("text-xs", item.paid ? "font-medium text-success" : "text-muted-foreground")}>
                  {t(`ui.landing.hero_bill.items.${item.key}.who`)}
                </span>
              </span>
              <span
                className={cn(
                  "text-sm tabular-nums",
                  item.paid ? "font-medium text-muted-foreground line-through" : "font-semibold"
                )}
              >
                {t(`ui.landing.hero_bill.items.${item.key}.amount`)}
              </span>
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-2 bg-muted px-5 py-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">{t("ui.landing.hero_bill.totals.subtotal_label")}</span>
            <span className="text-sm font-medium tabular-nums">
              {t("ui.landing.hero_bill.totals.subtotal_value")}
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">{t("ui.landing.hero_bill.totals.paid_label")}</span>
            <span className="text-sm font-medium tabular-nums text-success">
              {t("ui.landing.hero_bill.totals.paid_value")}
            </span>
          </div>
          <div className="flex items-baseline justify-between border-t pt-2">
            <span className="text-sm font-semibold">{t("ui.landing.hero_bill.totals.due_label")}</span>
            <span className="text-lg font-bold tracking-tight tabular-nums">
              {t("ui.landing.hero_bill.totals.due_value")}
            </span>
          </div>
        </div>
      </div>

      {/* A note on the comanda, not a floating panel. The card has no free
          interior room, and the system reserves shadows above shadow-sm for
          things that genuinely float and can be dismissed. */}
      <div className="mt-3 flex items-center gap-2.5 px-1">
        <span className="grid size-8 shrink-0 place-items-center rounded-md bg-muted">
          <Smartphone className="size-4" aria-hidden />
        </span>
        <span className="flex flex-col gap-px">
          <span className="text-xs font-semibold">{t("ui.landing.hero_bill.chip.title")}</span>
          <span className="text-xs text-muted-foreground">{t("ui.landing.hero_bill.chip.subtitle")}</span>
        </span>
      </div>
    </div>
  )
}
